/* ===========================================================
   Chatbot — Nethra Assist
   Scripted conversational helper. Handles:
     • order status lookup
     • product availability / pricing
     • payment confirmation
     • delivery tracking + handoff to support
   =========================================================== */
import React from "react";
import { findProduct } from "@/components/shell";
import { NETHRA_DATA } from "@/data/data";
import { Icons } from "@/lib/icons";

const { useState: cbUS, useEffect: cbUE, useRef: cbUR, useMemo: cbUM } = React;

const QUICK_FIRST = [
  "Track an order",
  "Check product availability",
  "Payment status",
  "Talk to a sales rep",
];

const TRENDING_CHIPS = [
  "Insulin Glargine",
  "Pulse Oximeter",
  "Amoxicillin 500mg",
  "Paracetamol drops",
];

const CAPABILITY_CARD = [
  { k: "📦 Orders",   v: "track · cancel · reschedule" },
  { k: "💊 Catalog",  v: "stock · price · alternatives" },
  { k: "💳 Payments", v: "receipts · refunds · GST" },
  { k: "🚚 Delivery", v: "live ETA · rider details" },
  { k: "👋 Humans",   v: "hand off to a sales rep" },
];

function timeNow() {
  const d = new Date();
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return h + ":" + m + " " + ampm;
}

function fmtInr(n) { return "₹" + Number(n || 0).toLocaleString("en-IN"); }

/* Build a contextual reply for an incoming user message. Returns either:
   - { text, card?, suggestions? }
   - an array of those if the bot wants to send a sequence. */
function botReply(input, state) {
  const D = NETHRA_DATA;
  const text = (input || "").toLowerCase().trim();

  // ---- explicit order id lookup
  const idMatch = text.match(/ns-?\d{4}-?\d{4}-?[a-z0-9]{4}/i);
  if (idMatch) {
    const id = idMatch[0].toUpperCase().replace(/\s/g, "");
    const order = D.userOrders.find((o) => o.id.toUpperCase().replace(/-/g, "") === id.replace(/-/g, ""));
    if (order) return orderStatusReply(order);
    return { text: `I couldn't find an order with ID ${id}. Could you double-check the reference? It looks like NS-YYYY-MMDD-XXXX.` };
  }

  // ---- intents
  if (/^(hi|hello|hey|hola|good\s+(morning|evening|afternoon))/.test(text)) {
    const first = (D.currentUser?.full_name || "there").split(" ")[0];
    return {
      text: `Hi ${first}! How can I help today? I can check order status, product stock, payment, or hand you off to a sales rep.`,
      suggestions: QUICK_FIRST,
    };
  }
  if (/track|status|order|where.*order/.test(text)) {
    const open = D.userOrders.filter((o) => o.status !== "delivered" && o.status !== "cancelled");
    if (open.length === 0) {
      return { text: "You don't have any open orders right now. Want help placing a new one?", suggestions: ["Browse catalog", "Check product availability"] };
    }
    const o = open[0];
    return orderStatusReply(o, true);
  }
  if (/availab|in.stock|stock|do.you.have|got/.test(text)) {
    return {
      text: "Sure — what product are you looking for? Try the brand or salt (e.g. *Insulin Glargine*, *Pulse Oximeter*, *Amoxicillin 500mg*).",
      suggestions: D.popularSearches.slice(0, 4),
    };
  }
  if (/pay|paid|payment|invoice|receipt/.test(text)) {
    const recent = D.userOrders[0];
    return paymentReply(recent);
  }
  if (/deliver|courier|rider|out.for|where.*driver|reach/.test(text)) {
    const order = D.userOrders.find((o) => o.status === "out_for_delivery") || D.userOrders[0];
    return deliveryReply(order);
  }
  if (/return|refund|damaged/.test(text)) {
    return {
      text: "Returns are open for 7 days from delivery on most lines. Damage claims are credited within 48 hours of evidence. Would you like to start a return?",
      suggestions: ["Start a return", "View refund policy"],
    };
  }
  if (/sales|rep|human|agent|person|call/.test(text)) {
    return {
      text: "Connecting you with a sales rep. Aarav Sharma usually replies within 4 minutes during business hours.\n\nIn the meantime, you can reach us at:",
      card: [
        { k: "Phone", v: "1800-419-7700" },
        { k: "Email", v: "support@nethrasap.in" },
        { k: "Reply window", v: "Mon-Sat · 9 AM – 7 PM IST" },
      ],
      suggestions: ["Wait for sales rep", "Back to bot"],
    };
  }
  if (/account|profile|address|password|sign\s?in/.test(text)) {
    return {
      text: "You can manage all that from your dashboard.",
      suggestions: ["My profile", "Addresses", "My orders"],
    };
  }
  if (/thank|thanks|cheers|great|nice/.test(text)) {
    return { text: "Happy to help. Anything else?", suggestions: QUICK_FIRST };
  }
  if (/cancel/.test(text)) {
    return { text: "Got it — cancellations are available before dispatch. Once an order is *out for delivery* we can only mark it as a refused-on-arrival return. Which order would you like to cancel?", suggestions: ["My orders"] };
  }

  // ---- fuzzy match a product name from the text
  if (text.length > 2) {
    const hit = D.products.find((p) =>
      p.name.toLowerCase().includes(text) ||
      p.brand.toLowerCase().includes(text) ||
      (p.sub_category || "").toLowerCase().includes(text)
    );
    if (hit) return productReply(hit);
  }

  // ---- fallback
  return {
    text: "I'm not sure I caught that. I can help with orders, product availability, payment, delivery tracking, or refunds. Try one of these:",
    suggestions: QUICK_FIRST,
  };
}

function orderStatusReply(order, isLatest) {
  const D = NETHRA_DATA;
  const stageLabel = {
    placed: "Order placed",
    confirmed: "Confirmed by warehouse",
    dispatched: "Dispatched · in transit",
    out_for_delivery: "Out for delivery",
    delivered: "Delivered",
    cancelled: "Cancelled",
  }[order.status] || order.status;
  const itemsLine = order.items.map((it) => {
    const p = findProduct(it.product_id);
    return p ? `· ${it.qty} × ${p.name}` : "";
  }).filter(Boolean).join("\n");

  return [
    {
      text: (isLatest ? "Here's your latest open order — " : "") +
            `Order ${order.id} is **${stageLabel}**.`,
      card: [
        { k: "Status",   v: stageLabel },
        { k: "Items",    v: order.items.length + " line items" },
        { k: "Total",    v: fmtInr(order.total) },
        { k: "ETA",      v: order.eta || "—" },
        { k: "Payment",  v: order.payment_method },
      ],
      suggestions: ["Track on map", "Payment status", "Need to cancel"],
    },
    { text: "Lines on this order:\n\n" + itemsLine + "\n\nNeed anything specific?", suggestions: ["Track on map", "Talk to sales rep"] },
  ];
}

function productReply(p) {
  const stockLabel = { in_stock: "in stock", low_stock: "low stock — only a few left", out_of_stock: "currently out of stock" }[p.stock_status];
  const stockText = p.stock_status === "in_stock"
    ? `✓ ${p.name} is **in stock** — ${p.delivery_time_mins || 30} min dispatch.`
    : p.stock_status === "low_stock"
      ? `⚠ ${p.name} is on **low stock**. I'd recommend placing an order soon.`
      : `✗ ${p.name} is **out of stock** right now. I can notify you when it returns.`;
  return {
    text: stockText,
    card: [
      { k: "Brand",    v: p.brand },
      { k: "Pack",     v: p.unit_label },
      { k: "Price",    v: fmtInr(p.price_min) + (p.price_max !== p.price_min ? "–" + fmtInr(p.price_max) : "") },
      { k: "Category", v: p.category_name },
    ],
    suggestions: p.stock_status === "out_of_stock"
      ? ["Notify me", "Suggest an alternative"]
      : ["Add to cart", "View product", "Place an order"],
  };
}

function paymentReply(order) {
  if (!order) return { text: "No recent orders to look up payment on. Want to start one?", suggestions: ["Browse catalog"] };
  const paid = order.status !== "cancelled";
  return {
    text: paid
      ? `Payment for order **${order.id}** has been **received and reconciled**.`
      : `The payment for order **${order.id}** was **refunded** after cancellation.`,
    card: [
      { k: "Order",   v: order.id },
      { k: "Method",  v: order.payment_method },
      { k: "Amount",  v: fmtInr(order.total) },
      { k: "Status",  v: paid ? "PAID · Reconciled" : "REFUNDED" },
      { k: "GSTIN",   v: "27AABCN9012P1Z3" },
    ],
    suggestions: ["Download invoice", "Email me the receipt", "Track order"],
  };
}

function deliveryReply(order) {
  if (!order) return { text: "No delivery to track right now." };
  if (order.status === "out_for_delivery") {
    return {
      text: `Order **${order.id}** is **out for delivery**. Your rider is Sandeep K. — ETA **${order.eta}**.`,
      card: [
        { k: "Carrier",   v: "Delhivery" },
        { k: "AWB",       v: "2840147219" },
        { k: "Rider",     v: "Sandeep K." },
        { k: "Rider ph.", v: "+91 98674 32109" },
        { k: "ETA",       v: order.eta },
      ],
      suggestions: ["Track on map", "Call rider", "Reschedule delivery"],
    };
  }
  return {
    text: `Order **${order.id}** is **${order.status.replace(/_/g, " ")}** — ETA ${order.eta}.`,
    suggestions: ["Track on map", "Talk to sales rep"],
  };
}

/* ----- Component ----- */
function ChatBubble({ msg }) {
  return (
    <div className={"chat-row " + (msg.from === "user" ? "is-user" : "is-bot")}>
      {msg.from === "bot" && <div className="chat-row-avatar">N</div>}
      <div>
        <div className="chat-bubble">
          {msg.text && <Markdownish text={msg.text} />}
          {msg.card && (
            <div className="chat-card">
              {msg.card.map((row, i) => (
                <div key={i} className="chat-card-row">
                  <span>{row.k}</span><span className="mono">{row.v}</span>
                </div>
              ))}
            </div>
          )}
          {msg.suggestions && (
            <div className="chat-suggestions">
              {msg.suggestions.map((s, i) => (
                <button key={i} className="chat-suggestion" onClick={() => msg.onSuggestion(s)}>{s}</button>
              ))}
            </div>
          )}
          <div className="chat-bubble-time">{msg.time}</div>
        </div>
      </div>
    </div>
  );
}

function Markdownish({ text }) {
  // Tiny **bold** + line-break renderer
  const parts = [];
  const lines = text.split("\n");
  lines.forEach((line, li) => {
    const tokens = line.split(/(\*\*[^*]+\*\*)/g);
    tokens.forEach((t, i) => {
      if (/^\*\*[^*]+\*\*$/.test(t)) parts.push(<strong key={li + "-" + i}>{t.slice(2, -2)}</strong>);
      else parts.push(<span key={li + "-" + i}>{t}</span>);
    });
    if (li < lines.length - 1) parts.push(<br key={"br-" + li} />);
  });
  return <>{parts}</>;
}

function ChatBot() {
  const [open, setOpen] = cbUS(false);
  const [messages, setMessages] = cbUS([]);
  const [input, setInput] = cbUS("");
  const [typing, setTyping] = cbUS(false);
  const scrollRef = cbUR(null);

  // seed welcome sequence — multi-bubble demo so the panel feels alive on open
  cbUE(() => {
    if (open && messages.length === 0) {
      const firstName = (NETHRA_DATA.currentUser?.full_name || "there").split(" ")[0];
      setTyping(true);
      setTimeout(() => {
        setTyping(false);
        pushBot([
          {
            text: `Hi ${firstName} 👋 — I'm **Nethra Assist**, your 24×7 concierge for everything Nethrasap.`,
          },
          {
            text: "Here's what I can help with right now:",
            card: CAPABILITY_CARD,
          },
          {
            text: "Tip: paste any order ID like **NS-2026-0512-A91F** and I'll fetch its live status. Or pick something to get started:",
            suggestions: QUICK_FIRST,
          },
          {
            text: "🔥 Trending searches today —",
            suggestions: TRENDING_CHIPS,
          },
        ]);
      }, 500);
    }
  }, [open]);

  cbUE(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, typing]);

  const pushUser = (text) => {
    setMessages((m) => [...m, { from: "user", text, time: timeNow(), id: Date.now() + Math.random() }]);
  };
  const pushBot = (payload) => {
    const arr = Array.isArray(payload) ? payload : [payload];
    arr.forEach((p, i) => {
      setTimeout(() => {
        setMessages((m) => [...m, { from: "bot", ...p, onSuggestion: handleSuggestion, time: timeNow(), id: Date.now() + Math.random() }]);
      }, i * 350);
    });
  };

  const send = (text) => {
    const v = (text || "").trim();
    if (!v) return;
    pushUser(v);
    setInput("");
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      const reply = botReply(v);
      pushBot(reply);
    }, 750 + Math.random() * 500);
  };

  const handleSuggestion = (s) => send(s);

  const unread = open ? 0 : 1;

  return (
    <>
      {!open && (
        <button className="chat-fab" onClick={() => setOpen(true)} aria-label="Open chatbot">
          <span className="chat-fab-pulse" />
          <Icons.Mail size={22} stroke={1.7} />
          {unread > 0 && <span className="chat-fab-badge">{unread}</span>}
        </button>
      )}
      {open && (
        <>
          <div className="chat-backdrop" onClick={() => setOpen(false)} />
          <div className="chat-drawer" role="dialog" aria-label="Chat with Nethra Assist">
            <header className="chat-head">
              <div className="chat-head-avatar">N</div>
              <div className="chat-head-text">
                <div className="chat-head-name">Nethra Assist
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, padding: "2px 6px", background: "var(--clay)", color: "#0a0a0a", borderRadius: 4, fontWeight: 700 }}>BETA</span>
                </div>
                <div className="chat-head-status">● Online · usually replies in seconds</div>
              </div>
              <button className="chat-head-close" onClick={() => setOpen(false)} aria-label="Close">
                <Icons.X size={16} stroke={2} />
              </button>
            </header>

            <div className="chat-body" ref={scrollRef}>
              {messages.map((m) => <ChatBubble key={m.id} msg={m} />)}
              {typing && (
                <div className="chat-row is-bot">
                  <div className="chat-row-avatar">N</div>
                  <div className="chat-bubble" style={{ padding: 0 }}>
                    <div className="chat-typing"><span /><span /><span /></div>
                  </div>
                </div>
              )}
            </div>

            <form className="chat-foot" onSubmit={(e) => { e.preventDefault(); send(input); }}>
              <input
                className="chat-input"
                placeholder="Type a message…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
              />
              <button className="chat-send" type="submit" disabled={!input.trim()} aria-label="Send">
                <Icons.ArrowR size={16} stroke={2.2} />
              </button>
            </form>
          </div>
        </>
      )}
    </>
  );
}

export { ChatBot };
